<div class="space-y-5">
  <button
    type="button"
    class="text-sm font-semibold text-secondary hover:underline"
    (click)="goBack()"
  >
    Back to Reading Lists
  </button>

  @if (loading()) {
    <div class="space-y-3">
      <div class="h-28 rounded-lg bg-slate-100 animate-pulse"></div>
      @for (item of [1, 2, 3]; track item) {
        <div class="h-20 rounded-lg bg-slate-100 animate-pulse"></div>
      }
    </div>
  } @else if (detail(); as current) {
    <section class="rounded-xl border border-slate-200 bg-white p-5">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <h2 class="text-xl font-bold text-gray-900">{{ current.summary.title }}</h2>
            @if (current.summary.completed) {
              <span class="badge-success">Completed</span>
            }
          </div>
          @if (current.summary.description) {
            <p class="mt-2 text-sm text-slate-600">{{ current.summary.description }}</p>
          }
          <div class="mt-3 flex flex-wrap gap-3 text-sm text-slate-600">
            <span>Assigned to {{ current.summary.assignedToName }}</span>
            <span>Created by {{ current.summary.createdByName }}</span>
            @if (current.summary.deadline) {
              <span class="font-medium text-amber-700">
                Deadline {{ current.summary.deadline | date: 'MMM d, yyyy' }}
              </span>
            }
          </div>
        </div>

        @if (current.summary.createdByCurrentUser) {
          <button type="button" class="btn-small-danger" (click)="askDelete()">Delete</button>
        }
      </div>

      <div class="mt-5">
        <div class="mb-1 flex items-center justify-between text-sm font-semibold text-slate-600">
          <span>{{ current.summary.readItems }} / {{ current.summary.totalItems }} read</span>
          <span>{{ current.summary.progressPercent }}%</span>
        </div>
        <div class="h-3 overflow-hidden rounded-full bg-slate-100">
          <div
            class="h-full rounded-full bg-secondary"
            [style.width.%]="current.summary.progressPercent"
          ></div>
        </div>
      </div>
    </section>

    @if (current.summary.createdByCurrentUser && current.summary.readers.length > 0) {
      <section class="rounded-xl border border-slate-200 bg-white p-5">
        <h3 class="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <i class="bi bi-people-fill mr-1"></i> Reader Progress
        </h3>

        <ul class="space-y-1.5">
          @for (
            reader of showAllReaders()
              ? current.summary.readers
              : current.summary.readers.slice(0, 3);
            track reader.userId
          ) {
            <li class="flex flex-col gap-1 rounded-lg px-2 py-1.5 sm:flex-row sm:items-center">
              <div class="min-w-0 flex-1">
                <span class="font-medium text-slate-700">
                  {{ reader.name }}
                  @if (reader.currentUser) {
                    <span
                      class="ml-1 rounded-full border border-violet-200 bg-violet-50 px-1.5 py-0.5 text-[10px] font-bold text-violet-600"
                      >You</span
                    >
                  }
                </span>
                <div class="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    class="h-full rounded-full bg-secondary"
                    [style.width.%]="reader.progressPercent"
                  ></div>
                </div>
              </div>
              <span class="text-xs text-slate-500">
                {{ readerProgressLabel(reader.readItems, reader.totalItems) }}
              </span>
            </li>
          }
        </ul>
        @if (current.summary.readers.length > 3) {
          <button
            type="button"
            (click)="showAllReaders.set(!showAllReaders())"
            class="mt-2 text-xs font-semibold text-secondary hover:underline"
          >
            {{
              showAllReaders() ? 'Show less' : 'Show all (' + current.summary.readers.length + ')'
            }}
          </button>
        }
      </section>
    }

    @if (errorMessage()) {
      <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {{ errorMessage() }}
      </div>
    }

    <section class="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div class="card-header">
        <h3 class="section-header-title">Papers</h3>
      </div>

      <div class="divide-y divide-slate-200">
        @for (item of current.items; track item.id) {
          <div class="list-item items-start">
            <div class="min-w-0 flex-1">
              <button
                type="button"
                class="text-left font-medium text-gray-900 hover:text-secondary hover:underline"
                (click)="openPaper(item)"
              >
                {{ item.title || 'Untitled' }}
              </button>
              <p class="mt-1 text-sm text-slate-500">
                {{ item.venue || 'Unknown venue' }}
                @if (item.year) {
                  <span> · {{ item.year }}</span>
                }
              </p>
              @if (item.authors) {
                <p class="mt-1 text-xs text-slate-500">{{ item.authors }}</p>
              }
            </div>

            <div class="flex-shrink-0">
              @if (current.summary.assignedToCurrentUser) {
                <div class="flex flex-wrap gap-2" role="group" aria-label="Reading status">
                  @for (status of ['TO_READ', 'IN_PROGRESS', 'READ']; track status) {
                    <button
                      type="button"
                      class="rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors"
                      [ngClass]="statusButtonClass(item, $any(status))"
                      [disabled]="updatingItemId() === item.id"
                      [attr.aria-pressed]="item.status === status"
                      (click)="updateStatus(item, $any(status))"
                    >
                      {{ statusLabel($any(status)) }}
                    </button>
                  }
                </div>
              } @else {
                <span class="badge-primary">{{ statusLabel(item.status) }}</span>
              }
            </div>
          </div>
        }
      </div>
    </section>
  } @else if (errorMessage()) {
    <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {{ errorMessage() }}
    </div>
  }
</div>

<app-base-modal
  [open]="!!selectedPaper()"
  (openChange)="closePaper()"
  [title]="selectedPaper()?.title || 'Paper'"
  size="2xl"
>
  @if (selectedPaper(); as paper) {
    @if (paperLoading()) {
      <div
        class="flex h-[84vh] items-center justify-center rounded-lg border border-slate-200 text-sm text-slate-600"
      >
        Loading PDF...
      </div>
    } @else if (paperViewerUrl(); as viewerUrl) {
      <iframe
        class="h-[84vh] w-full rounded-lg border border-slate-200"
        [src]="viewerUrl"
        title="Paper viewer"
      ></iframe>
    } @else {
      <div class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        {{ paperError() || 'No readable PDF is attached to this paper.' }}
      </div>
    }
  }
</app-base-modal>

<app-confirm-modal
  [open]="showDeleteConfirm()"
  title="Delete reading list"
  message="This reading list and its progress will be deleted."
  confirmText="Delete"
  cancelText="Cancel"
  confirmVariant="danger"
  [loading]="deleting()"
  (openChange)="showDeleteConfirm.set($event)"
  (confirmed)="deleteReadingList()"
  (canceled)="cancelDelete()"
></app-confirm-modal>
